import { Formik } from 'formik';
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from 'yup';
import { useAuth } from "../../../../../hooks/useAuth";
import useAddBooking from "../../../../../hooks/TiassistoHooks/useAddBooking";
import { useApiClient } from '../../../../../hooks/useApiClient';
import { useTranslations } from '../../../../../hooks/useTranslations';
import dayjs from "dayjs";
import toaster from '../../../../../utils/toaster';
import { useEffect, useMemo, useState } from 'react';
import Select, { SingleValue } from 'react-select';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DummyDataCategories from "./dummy-data-categories.json";
import { OptionType, TiassistoMaintenanceSubCategoriesReponseDTO, TiassistoMaintenanceCategoriesReponseDTO } from '../../../../../api';
import { useTranslation } from 'react-i18next';
import useTiassistoStore from '../../../../../store/TiassistoStore';

import _ from 'lodash';
import { TiassistoAddBookingRequestDTO } from '../../../../../api/models/TiassistoModels/TiassistoAddBookingRequestDTO';
import SpinnerButton from '../../../../SpinnerButton/SpinnerButton';
import RequiredFormLabel from 'src/components/RequiredFormLabel/RequiredFormLabel';

interface CreateNewPudProps {
  licensePlate?: string;
  onClose?: () => void;
}

interface CreateNewPudFormValues {
  bookMaintenanceCategoryId: string;
  bookMaintenanceSubCategoryId: string;
  maintenanceDate: string;
  maintenanceTime: string;
  pudAddress: string;
  pudCity: string;
  pudDistrict: string;
  pudPostalCode: string;
  note: string;
}

const CreateNewPud = ({ licensePlate, onClose }: CreateNewPudProps) => {
  const { user } = useAuth();
  const { apiClient, handleApi } = useApiClient();
  const { t, i18nT } = useTranslations()
  const [categoriesOptions, setCategoriesOptions] = useState<OptionType[]>([])
  const [subCategoriesOptions, setSubCategoriesOptions] = useState<OptionType[]>([])
  const [categoryId, setCategoryId] = useState<string>('')
  const queryClient = useQueryClient();
  const { addBooking, savePudResponse, isSavePudPending, isSavePudError } = useAddBooking(licensePlate, onClose);
	const { dieStatusData } = useTiassistoStore();

  const initialValues = useMemo(() => ({
    bookMaintenanceCategoryId: '',
    bookMaintenanceSubCategoryId: '',
    maintenanceDate: '',
    maintenanceTime: '',
    pudAddress: dieStatusData?.pudDetails?.pudAddress ?? '',
    pudCity: dieStatusData?.pudDetails?.pudCity ?? '',
    pudDistrict: dieStatusData?.pudDetails?.pudDistrict ?? '',
    pudPostalCode: dieStatusData?.pudDetails?.pudPostalCode ?? '',
    note: ''
  }), []);

  const validationSchema = Yup.object().shape({
    bookMaintenanceCategoryId: Yup.string().required(t('category_is_mandatory')),
    bookMaintenanceSubCategoryId: Yup.string().required(t('subcategory_is_mandatory')),
    maintenanceDate: Yup.string().required(t('maintenance_date_is_mandatory')),
    maintenanceTime: Yup.string().required(t('maintenance_time_is_mandatory')),
    pudAddress: Yup.string().required(t('pudaddress_is_mandatory')),
    pudCity: Yup.string().required(t('pudcity_is_mandatory')),
    pudDistrict: Yup.string().required(t('puddistrict_is_mandatory')),
    pudPostalCode: Yup.string().required(t('pudpostalcode_is_mandatory')),
    note: Yup.string(),
  });

  // query tiassisto to get maintanance categories
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useQuery<TiassistoMaintenanceCategoriesReponseDTO>({
    queryKey: ['getMaintananceCategories'],
    queryFn: () => apiClient.tiassistoController.getPudMaintenanceCategories(),
    retry: false,
  });

  useEffect(() => {
    if (categoriesData && !_.isEmpty(categoriesData.categories)) {
      const options = categoriesData.categories.map(cat => ({
        value: cat.id,
        label: cat.name,
      }));

      setCategoriesOptions(options);
    }
  }, [categoriesData]);

  // get sub-categories from categoryId
  const { data: subCategoriesData, isLoading: subCategoriesLoading, error: subCategoriesError } = useQuery<TiassistoMaintenanceSubCategoriesReponseDTO>({
    queryKey: ['getSubMaintenanceCategories', categoryId],
    queryFn: () => apiClient.tiassistoController.getPudMaintenanceSubCategories(categoryId),
    enabled: !!categoryId,
    retry: false,
  });

  useEffect(() => {
    if (subCategoriesData && !_.isEmpty(subCategoriesData.subcategories)) {
      const options = subCategoriesData.subcategories.map(cat => ({
        value: cat.id,
        label: cat.name,
      }));

      setSubCategoriesOptions(options);
    }
  }, [subCategoriesData]);


  const handleCreateNewPud = (data: CreateNewPudFormValues) => {
    const { maintenanceDate, ...otherData } = data;

    const payload: TiassistoAddBookingRequestDTO = {
      licensePlate: licensePlate ?? '',
      log: {
        operator: user?.username ?? '',
        description: 'Creazione di una nuova richiesta PUD',
      },
      maintenanceDate: dayjs(data.maintenanceDate).format('DD/MM/YYYY'),
      ...otherData
    }

    addBooking(payload)
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        handleCreateNewPud(values);
      }}
    >
      {({
        errors,
        values,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Row className="m-0">
              {/* bookMaintenanceCategoryId */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('category')} />
                  <Select
                    menuPosition="fixed"
                    placeholder={t('select')}
                    options={categoriesOptions}
                    name="bookMaintenanceCategoryId"
                    onChange={(option: SingleValue<OptionType>) => {
                      setFieldValue("bookMaintenanceCategoryId", option?.value);
                      setCategoryId(option?.value || "");
                    }}
                    onBlur={() => setFieldTouched("bookMaintenanceCategoryId", true)}
                    value={categoriesOptions.find(
                      (option) => option.value === values.bookMaintenanceCategoryId
                    )}
                  />
                  {errors.bookMaintenanceCategoryId &&
                    touched.bookMaintenanceCategoryId && (
                      <div className="invalid-feedback">
                        {errors.bookMaintenanceCategoryId}
                      </div>
                    )}
                </Form.Group>
              </Col>

              {/* bookMaintenanceSubCategoryId */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('subcategory')} />
                  <Select
                    menuPosition="fixed"
                    placeholder={t('select')}
                    options={subCategoriesOptions}
                    name="bookMaintenanceSubCategoryId"
                    onChange={(option: SingleValue<OptionType>) => {
                      setFieldValue("bookMaintenanceSubCategoryId", option?.value);
                    }}
                    onBlur={() =>
                      setFieldTouched("bookMaintenanceSubCategoryId", true)
                    }
                    value={subCategoriesOptions.find(
                      (option) => option.value === values.bookMaintenanceSubCategoryId
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="m-0">
              {/* maintenanceDate */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('maintenance_date')} />
                  <Form.Control
                    type="date"
                    name="maintenanceDate"
                    onChange={handleChange}
                    value={values.maintenanceDate}
                    isInvalid={!!errors.maintenanceDate && touched.maintenanceDate}
                  />
                </Form.Group>
              </Col>

              {/* maintenanceTime */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('preferred_time')} />
                  <Form.Control
                    type="time"
                    name="maintenanceTime"
                    onChange={handleChange}
                    value={values.maintenanceTime}
                    isInvalid={!!errors.maintenanceTime && touched.maintenanceTime}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="m-0">
              {/* pudAddress */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('pudAddress')} />
                  <Form.Control
                    type="text"
                    name="pudAddress"
                    onChange={handleChange}
                    value={values.pudAddress}
                    isInvalid={!!errors.pudAddress && touched.pudAddress}
                  />
                </Form.Group>
              </Col>

              {/* pudCity */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('pudCity')} />
                  <Form.Control
                    type="text"
                    name="pudCity"
                    onChange={handleChange}
                    value={values.pudCity}
                    isInvalid={!!errors.pudCity && touched.pudCity}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="m-0">
              {/* pudDistrict */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('pudDistrict')} />
                  <Form.Control
                    type="text"
                    name="pudDistrict"
                    onChange={handleChange}
                    value={values.pudDistrict}
                    isInvalid={!!errors.pudDistrict && touched.pudDistrict}
                  />
                </Form.Group>
              </Col>

              {/* pudPostalCode */}
              <Col sm={6} className="pr-4">
                <Form.Group className="mb-3">
                  <RequiredFormLabel label={t('pudPostalCode')} />
                  <Form.Control
                    type="text"
                    name="pudPostalCode"
                    onChange={handleChange}
                    value={values.pudPostalCode}
                    isInvalid={!!errors.pudPostalCode && touched.pudPostalCode}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="m-0">
              {/* Note */}
              <Col sm={12} className="pr-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t('notes')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="note"
                    onChange={handleChange}
                    value={values.note}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                type="submit"
                disabled={isSavePudPending}
              >
                <SpinnerButton
                  label={t('save')}
                  busy={isSavePudPending}
                />
              </Button>

            </div>
          </Form>
        )
      }}
    </Formik>
  );
};

export default CreateNewPud;
